import React, { memo, useState } from 'react';

import { Button, TextField } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import { Auth } from 'aws-amplify';
import { useTranslation } from 'react-i18next';

const ForgotPassword = props => {
    const { t } = useTranslation();

    const [model, setModel] = useState({
        email: '',
        code: '',
        password: '',
    });

    const [confirm, setConfirm] = useState(false);
    const [userException, setUserException] = useState(null);
    const [resetException, setResetException] = useState(null);

    const sendCode = async model => {
        try {
            const email = model.email && model.email.trim();

            if (email) {
                await Auth.forgotPassword(email);
                setConfirm(true);
                setUserException(null);
            }
        } catch (error) {
            setUserException(error);
        }
    };

    const sendNewPassword = async model => {
        try {
            const email = model.email && model.email.trim();
            const password = model.password && model.password.trim();
            const code = model.code && model.code.trim();

            if (email && code && password) {
                await Auth.forgotPasswordSubmit(email, code, password);
                props.onStateChange('signIn', {});
                setResetException(null);
            }
        } catch (error) {
            setResetException(error);
        }
    };

    const handleValueChange = (path, event) => {
        const value = event.target?.value || '';

        setModel(prev => {
            return {
                ...prev,
                [path]: value,
            };
        });
    };

    const handleBackClick = () => {
        props.onStateChange('signIn', {});
    };

    const handleSubmit = event => {
        if (event.charCode === 13) {
            if (confirm) {
                sendNewPassword(model);
            } else {
                sendCode(model);
            }
        }
    };

    const getException = exception => {
        if (exception) {
            if (exception.code === 'CodeMismatchException') {
                return {
                    codeException: exception.message,
                };
            }

            if (exception.code === 'InvalidParameterException') {
                return {
                    passwordException: t(`authentication.validation.password.invalidFormat`),
                };
            }

            return {
                generalException: exception.message,
            };
        }
        return null;
    };

    const resetExceptionMessage = getException(resetException);

    return (
        <div className="login-info-outer-container">
            <div className="login-info-inner-container" onKeyPress={handleSubmit}>
                <div className="login-info-label">{t(`authentication.forgotPassword.label`)}</div>
                {!confirm && (
                    <React.Fragment>
                        <div className={`input ${userException ? 'error' : ''}`}>
                            <TextField
                                label={t(`authentication.forgotPassword.email`)}
                                value={model.email || ''}
                                onChange={handleValueChange.bind({}, 'email')}
                            />
                            {userException && (
                                <div className="c-error">
                                    <ErrorIcon />
                                    {userException.message}
                                </div>
                            )}
                        </div>
                        <div className="login-info-actions">
                            <div className="action-link" onClick={handleBackClick}>
                                {t(`authentication.forgotPassword.back`)}
                            </div>
                            <div className="action-button">
                                <Button onClick={sendCode.bind({}, model)}>{t(`authentication.forgotPassword.send`)}</Button>
                            </div>
                        </div>
                    </React.Fragment>
                )}
                {confirm && (
                    <React.Fragment>
                        <div className={`input ${resetExceptionMessage?.codeException ? 'error' : ''}`}>
                            <TextField
                                label={t(`authentication.forgotPassword.code`)}
                                value={model.code || ''}
                                onChange={handleValueChange.bind({}, 'code')}
                            />
                            {resetExceptionMessage?.codeException && (
                                <div className="c-error">
                                    <ErrorIcon />
                                    {resetExceptionMessage.codeException}
                                </div>
                            )}
                        </div>
                        <div className={`input ${resetExceptionMessage?.passwordException ? 'error' : ''}`}>
                            <TextField
                                label={t(`authentication.forgotPassword.password`)}
                                type="password"
                                autoComplete="current-password"
                                value={model.password || ''}
                                onChange={handleValueChange.bind({}, 'password')}
                            />
                            {resetExceptionMessage?.passwordException && (
                                <div className="c-error">
                                    <ErrorIcon />
                                    {resetExceptionMessage.passwordException}
                                </div>
                            )}
                        </div>
                        {resetExceptionMessage?.generalException && (
                            <div className="c-error">
                                <ErrorIcon />
                                {resetExceptionMessage.generalException}
                            </div>
                        )}
                        <div className="login-info-actions">
                            <div className="action-link" onClick={sendCode.bind({}, model)}>
                                {t(`authentication.forgotPassword.resendCode`)}
                            </div>
                            <div className="action-button">
                                <Button onClick={sendNewPassword.bind({}, model)}>{t(`authentication.forgotPassword.submit`)}</Button>
                            </div>
                        </div>
                    </React.Fragment>
                )}
            </div>
        </div>
    );
};

export default memo(ForgotPassword);
